/**
 * MaterialSamplesList — "Material Samples" section (Figma parity, node 1657:10958).
 * Shared by chapters 2726, 2727, 2729 (CX-2725 §3).
 *
 * Each sample renders as a card: a large product-render preview (360×360) on the
 * left, and on the right a small swatch/icon (40×40) + grade name + description
 * + the shared CX-2764 ContentLink CTA ("Check it now"). When no product image
 * is supplied the preview shows a neutral placeholder (matches the Figma frame).
 */

import { ContentLink } from "components/content-link";
import type {
  ChapterMaterialSample,
  MaterialSampleStatus,
} from "../chapter.types";
import { CMF_GUIDE_DEFAULT_IMAGE } from "../cmfGuideData";
import "./MaterialSamplesList.css";

interface MaterialSamplesListProps {
  samples: ChapterMaterialSample[];
  title?: string;
}

const STATUS_LABEL: Record<MaterialSampleStatus, string> = {
  available: "Available",
  "coming-soon": "Coming soon",
  unavailable: "Unavailable",
};

const Spec = ({ label, value }: { label: string; value: string }) => (
  <>
    <span className="material-samples-list__spec-label">{label}:</span> {
      value
    }{" "}
  </>
);

export const MaterialSamplesList = ({
  samples,
  title,
}: MaterialSamplesListProps) => (
  <section className="material-samples-list">
    {title && (
      <div className="material-samples-list__header">
        <h3 className="material-samples-list__title">{title}</h3>
        <span className="material-samples-list__count">
          {samples.length} {samples.length === 1 ? "color" : "colors"}
        </span>
      </div>
    )}
    <ul className="material-samples-list__items">
      {samples.map((sample) => (
        <li key={sample.name} className="material-samples-list__item">
          {/* Left — large product-render preview */}
          <div className="material-samples-list__render">
            {sample.productImage && (
              <img
                src={sample.productImage}
                alt={sample.name}
                className="material-samples-list__render-img"
                loading="lazy"
                draggable={false}
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  if (img.src.endsWith(CMF_GUIDE_DEFAULT_IMAGE)) return;
                  img.src = CMF_GUIDE_DEFAULT_IMAGE;
                }}
              />
            )}
          </div>

          {/* Right — swatch + name + spec + CTA */}
          <div className="material-samples-list__details">
            <div className="material-samples-list__detail-row">
              <span
                className="material-samples-list__swatch"
                style={
                  sample.colorHex
                    ? { backgroundColor: sample.colorHex }
                    : undefined
                }
                aria-hidden="true"
              />
              <div className="material-samples-list__text">
                <div className="material-samples-list__name-row">
                  <span className="material-samples-list__name">
                    {sample.name}
                  </span>
                  {sample.status && (
                    <span
                      className={`material-samples-list__status material-samples-list__status--${sample.status}`}
                      title={STATUS_LABEL[sample.status]}
                    >
                      <span
                        className="material-samples-list__dot"
                        aria-hidden="true"
                      />
                      {STATUS_LABEL[sample.status]}
                    </span>
                  )}
                </div>

                {sample.description && (
                  <p className="material-samples-list__spec">
                    {sample.description}
                  </p>
                )}

                {(sample.ralColor ||
                  sample.material ||
                  sample.opacity ||
                  sample.surfaceTexture) && (
                  <p className="material-samples-list__spec">
                    {sample.ralColor && (
                      <Spec label="Color" value={sample.ralColor} />
                    )}
                    {sample.material && (
                      <Spec label="Material" value={sample.material} />
                    )}
                    {sample.opacity && (
                      <Spec label="Opacity" value={sample.opacity} />
                    )}
                    {sample.surfaceTexture && (
                      <Spec
                        label="Surface Texture"
                        value={sample.surfaceTexture}
                      />
                    )}
                  </p>
                )}

                {sample.link && <ContentLink {...sample.link} />}
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  </section>
);
